<template>
  <div>
    <b-row class="mt-3">
      <b-col>
        <h6 v-if="!hideLabel">{{ trans('added_to_cart') }}</h6>
        <span v-if="hideLabel">&nbsp;</span>
      </b-col>
    </b-row>
    <b-row class="ml-3 mb-3">
      <b-col v-if="invoice.status != 'finalized' && !package_invoice_item.expired && invoice.status != 'expired'" class="pl-0">
        <b-button variant="success" size="sm" disabled>
          <i v-if="!busy" class="fa fa-fw fa-check"></i>
          <i v-if="busy" class="fa fa-fw fa-circle-notch fa-spin"></i>
        </b-button>
        <span class="ml-2">
          {{ package_invoice_item.booked_location_summary }}
        </span>
        <span v-if="pkg.requires_coupon">
          (<small><i class="fa fa-ticket-alt text-muted"></i></small> {{ package_invoice_item.coupon.code }})
        </span>
        <dollar :value="playerOnePrice" class="ml-1 lead font-weight-bold" />
        <b-button variant="link" size="sm" class="ml-2 text-danger" :disabled="busy" @click="removeFromCart(package_invoice_item)" :title="trans('remove_from_cart')">
          <i class="fa fa-fw fa-times"></i>
        </b-button>
      </b-col>
      <b-col v-if="invoice.status != 'finalized' && package_invoice_item.expired || invoice.status == 'expired'" class="px-0">
        <b-button variant="danger" size="sm" :disabled="busy" @click="updateItemInCart(package_invoice_item)">
          <i v-if="!busy" class="fa fa-fw fa-exclamation"></i>
          <i v-if="busy" class="fa fa-fw fa-circle-notch fa-spin"></i>
        </b-button>
        <span class="ml-2">
          <i class="fa fa-fw fa-exclamation-triangle"></i>
          {{ trans('booking_expired_add_to_cart') }}
        </span>
        <b-button variant="link" size="sm" class="ml-2 text-danger" :disabled="busy" @click="removeFromCart(package_invoice_item)" :title="trans('remove_from_cart')">
          <i class="fa fa-fw fa-times"></i>
        </b-button>
      </b-col>
      <b-col v-if="invoice.status == 'finalized'" class="pl-0">
        <span class="ml-2">
          <i class="fa fa-fw fa-check-circle"></i>
          {{ package_invoice_item.booked_location_summary }}
          {{ trans('booking_paid') }}
        </span>
      </b-col>
    </b-row>

    <b-row v-if="!pkg.requires_coupon && pkg.price > 0" class="ml-3 mb-2">
      <b-col class="pl-0 col-auto">
        <b-button variant="outline-info" size="sm" :disabled="busyOrExpired" @click="show_apply_coupon = !show_apply_coupon">
          <i :class="`fa fa-fw fa-${package_invoice_item.promo_code ? 'tag' : 'ticket-alt'}`"></i>
        </b-button>
      </b-col>
      <b-col class="px-0">
        <div v-if="!show_apply_coupon">
          <span v-if="!!package_invoice_item.coupon">
            {{ trans('coupon') }} {{ package_invoice_item.coupon.code }}
            <dollar :value="-package_invoice_item.discount" class=" ml-1 lead font-weight-bold" />
          </span>
          <span v-if="!!package_invoice_item.promo_code">
            {{ trans('promo_code') }} {{ package_invoice_item.promo_code.code }}
            <dollar :value="-package_invoice_item.discount" class=" ml-1 lead font-weight-bold" />
          </span>
          <span v-if="!package_invoice_item.coupon && !package_invoice_item.promo_code">
            {{ trans('add_a_coupon') }}
          </span>
          <b-button v-if="!!package_invoice_item.coupon || !!package_invoice_item.promo_code" variant="link" size="sm" class="text-danger" :disabled="busyOrExpired" @click="removeCoupon(package_invoice_item)" :title="trans('remove_from_cart')">
            <i class="fa fa-fw fa-times"></i>
          </b-button>
        </div>
        <div v-if="show_apply_coupon" class="d-inline-block">
          <apply-coupon
            :organization="organization"
            :invoice="invoice"
            :invoice_item="package_invoice_item"
            :coupon="coupon"
            @close="onCloseApplyCoupon"
            @invoice="onCouponApplied"
          ></apply-coupon>
        </div>
        <p v-if="nbCoupons > 0">
          {{ trans('you_have_coupons_left', {nb: nbCoupons}) }}
          <b-button
            v-if="showApplyExisitingCoupon"
            variant="outline-branded"
            size="sm"
            class="py-0 ml-2"
            @click="onClickApplyExistingCoupon"
            :disabled="busy"
          >
            {{ trans('apply') }}
          </b-button>
        </p>
        <b-row v-if="canShowConnect">
          <b-col>
            <p>
              {{ trans('client_connect_to_apply_coupon') }}
              <b-button
                variant="outline-branded"
                size="sm"
                class="py-0 ml-2"
                @click="show_connect = !show_connect"
                :disabled="busy"
              >
                {{ trans(show_connect ? 'hide' : 'connect') }}
              </b-button>
            </p>
            <connect
              v-if="show_connect"
              :organization="organization"
              :invoice="invoice"
              :reload-on-success="false"
              @connected="onConnected"
            ></connect>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row v-if="pkg.force_choose_players" class="mt-3 mb-2">
      <b-col>
        <label class="d-block">
          {{ trans('first_player_account_owner') }}
        </label>
        <address v-if="!!invoice && !!invoice.recipient" class="ml-2">
          <strong v-if="invoice.recipient.name" class="d-block">{{ invoice.recipient.name }}</strong>
          <span v-if="invoice.recipient.email" class="d-block">{{ invoice.recipient.email }}</span>
          <span v-if="invoice.recipient.mobile" class="d-block">{{ invoice.recipient.mobile }}</span>
          <membership-text v-if="!!invoiceRecipientMembership" :membership="invoiceRecipientMembership" size="sm"></membership-text>
        </address>
      </b-col>
    </b-row>

    <b-row v-if="showAdditionalPlayers" class="mb-2">
      <b-col cols="12" cols-lg="6">
        <label>
          {{ trans('how_many_players') }}
          <small class="text-muted" :title="trans('price_per_player_about')" v-b-tooltip.hover>
            <i class="fa fa-fw fa-info-circle"></i>
          </small>
        </label>
        <b-form-group v-if="pkg.ask_for_players == 'singles_or_doubles'">
          <b-form-radio-group
            v-model="singles_or_doubles"
            :options="singleOrDoublesOptions"
            :disabled="busyOrExpired"
            @change="onChangeSinglesOrDoubles"
          ></b-form-radio-group>
        </b-form-group>
        <b-form-group v-if="pkg.ask_for_players == 'up_to_four'">
          <b-form-radio-group
            v-model="up_to_four"
            :options="upToFourOptions"
            :disabled="busyOrExpired"
            @change="onChangeSinglesOrDoubles"
          ></b-form-radio-group>
        </b-form-group>

        <b-form-group v-if="pkg.force_choose_players || pkg.adjust_price_per_player" v-for="(addon, index) in player_addons" :key="'player_addon_'+index" class="ml-3 mb-0">
          <b-form-select
            v-if="!pkg.force_choose_players || !addon.form_question_id"
            v-model="addon.addon_id"
            size="sm"
            style="width: 200px;"
            :options="playerAddonOptions"
            class="mb-2"
            :disabled="busyOrExpired"
            @change="onChangePlayerAddon(index, $event)"
          ></b-form-select>
          <b-button
            v-if="pkg.force_choose_players && !!addon.form_question_id"
            :variant="`${!!addon.form_answer_id ? 'link' : 'outline-branded'}`"
            :size="!addon.form_answer_id ? 'sm' : 'md'"
            class="text-left mb-2"
            style="width: 200px; overflow: hidden;"
            :disabled="busyOrExpired"
            @click="onClickChoosePlayer(addon)"
          >
            <small><i :class="`fa fa-fw fa-${!!addon.form_answer_id ? 'user' : 'edit'}`"></i></small>
            {{ !!addon.form_answer_id ? addon.name.replace(`(${addon.addon_name})`, '') : trans('choose_a_player') }}
          </b-button>
          <label v-if="pkg.adjust_price_per_player" class="ml-2 mb-2">
            {{ trans(addon.addon_name) }}
            <dollar :value="addon.price" class="lead font-weight-bold"></dollar>
          </label>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="addons.length > 0">
      <b-col cols="12" cols-lg="6">
        <label>{{ trans('add_ons') }}</label>
        <b-form-group v-for="(addon, index) in addons" :key="addon.id" class="ml-3">
          <b-button v-if="!!addon.invoice_item_id" variant="success" size="sm" disabled>
            <i class="fa fa-fw fa-check"></i>
          </b-button>
          <label for="`addon_${addon.id}`" class="ml-2">
            {{ addon.name }}
          </label>
          <b-form-input
            type="number"
            v-model="addon.quantity"
            size="sm"
            inline
            :min="addon.min_quantity || 0"
            :max="addon.max_quantity || 100"
            :disabled="busyOrExpired"
            class="d-inline-block ml-2"
            :state="getAddonQuantityState(package_invoice_item, addon)"
            style="width: inherit;"
          />
          <span>x</span>
          <dollar v-model="addon.price" class="lead font-weight-bold" />
          <b-button
            v-if="!addon.invoice_item_id && addon.quantity > 0"
            variant="branded"
            size="sm"
            class="ml-2"
            :disabled="busyOrExpired"
            @click="onAddAddonToCart(package_invoice_item, addon)"
          >
            <i class="fa fa-fw fa-plus"></i>
          </b-button>
          <b-button
            v-if="!!addon.invoice_item_id && !addon.required"
            variant="link"
            size="sm"
            class="ml-2 text-danger"
            :disabled="busyOrExpired"
            @click="onRemoveAddonFromCart(package_invoice_item, addon)"
          >
            <i class="fa fa-fw fa-times"></i>
          </b-button>
          <b-button
            v-if="!!addon.invoice_item_id && (addon.original_quantity != addon.quantity || getAddonQuantityState(package_invoice_item, addon) === false)"
            variant="branded"
            size="sm"
            class="ml-2"
            :disabled="busyOrExpired"
            @click="onModifyAddonQuantityInCart(package_invoice_item, addon)"
          >
            <i class="fa fa-fw fa-check d-md-none"></i>
            <span class="d-none d-md-inline">{{ trans('apply') }}</span>
          </b-button>
          <b-form-text v-if="!!addon.invoice_item_id && addon.required && getAddonQuantityState(package_invoice_item, addon) === false" class="ml-5">
            <span class="text-danger">{{ trans('please_provide_quantity') }}</span>
            <span class="text-danger" v-if="addon.min_quantity">{{ trans('minimum') }} {{ addon.min_quantity }}.</span>
            <span class="text-danger" v-if="addon.max_quantity">{{ trans('maximum') }} {{ addon.max_quantity }}.</span>
          </b-form-text>
        </b-form-group>
      </b-col>
    </b-row>

    <package-reservation-choose-player-modal
      :organization="organization"
      :pkg="pkg"
      :invoice="invoice"
      :invoiceItem="player_addon_invoice_item"
      @invoice="$emit('invoice', $event)"
    ></package-reservation-choose-player-modal>
  </div>
</template>

<script>
  import Dollar from './../billing/Dollar'
  import ApplyCoupon from './PackageApplyCoupon'
  import PackageReservationChoosePlayerModal from './PackageReservationChoosePlayerModal'
  import Connect from './../client/Connect'
  import MembershipText from './../membership/MembershipText'

  function isAddonHidden(addon, addon_rules, booked_events) {
    return _.reduce(addon_rules || [], (hidden, rule) => {
      if (rule.addon_id == addon.id) {
        if (rule.type == 'location') {
          if (!!_.find(booked_events, {location_id: rule.location_id})) {
            hidden = true
          }
        }
        else if (rule.type == 'time' || rule.type == 'weekdays') {
          return _.reduce(booked_events, (hidden, e) => {
            let event_start = moment(e.start)
            let time_start = moment(event_start.format('YYYY-MM-DD') + ' ' + rule.start, 'YYYY-MM-DD HH:mm:ss')
            let time_end = moment(event_start.format('YYYY-MM-DD') + ' ' + rule.end, 'YYYY-MM-DD HH:mm:ss')
            if (event_start.isSameOrAfter(time_start) && event_start.isBefore(time_end)) {
              if (rule.type == 'time') {
                hidden = true
              }
              else if (rule.type == 'weekdays') {
                let weekday = _.isoWeekdayToEnglish(event_start.isoWeekday())
                if ((rule.weekdays || []).indexOf(weekday) >= 0) {
                  hidden = true
                }
              }
            }
            return hidden
          }, hidden)
        }
      }
      return hidden
    }, false)
  }

  export default {
    props: {
      organization: Object,
      pkg: Object,
      availability: Object,
      invoice: Object,
      package_invoice_item: Object,
      hideLabel: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      Dollar,
      ApplyCoupon,
      PackageReservationChoosePlayerModal,
      Connect,
      MembershipText,
    },
    data() {
      return {
        trans: trans,
        moment: moment,
        numeral: numeral,
        lang: window.lang,
        currency: _.currency,
        personToHtml: _.personToHtml,
        error: null,
        busy: false,
        show_apply_coupon: false,
        addons: [],
        modifier_addons: [],
        player_addons: [],
        singles_or_doubles: null,
        up_to_four: null,
        coupon: null,
        player_addon_invoice_item: null,
        show_connect: false,
      }
    },
    computed: {
      busyOrExpired() {
        return this.busy ||
          !this.package_invoice_item ||
          this.package_invoice_item.expired ||
          this.invoice.status == 'expired'
      },
      nbCoupons() {
        return !!this.invoice.available_coupons_per_package &&
          !!this.invoice.available_coupons_per_package[this.pkg.id] ?
          this.invoice.available_coupons_per_package[this.pkg.id] :
          0
      },
      showNbCoupons() {
        return this.nbCoupons > 0 &&
          (this.show_apply_coupon || !!this.package_invoice_item.coupon)
      },
      showApplyExisitingCoupon() {
        return this.invoice.status != 'expired' &&
          !!this.pkg &&
          !this.pkg.requires_coupon &&
          !!this.package_invoice_item &&
          !this.package_invoice_item.coupon &&
          this.package_invoice_item.price > 0 &&
          this.nbCoupons > 0 &&
          this.show_apply_coupon
      },
      canShowConnect() {
        return this.invoice.status != 'expired' &&
          !!this.pkg &&
          !this.pkg.requires_coupon &&
          !!this.package_invoice_item &&
          this.package_invoice_item.price > 0 &&
          !this.organization.connected_client &&
          this.show_apply_coupon
      },
      invoiceRecipientMembership() {
        if (!this.invoice ||
          !this.invoice.recipient ||
          !this.invoice.recipient.extra ||
          !this.invoice.recipient.extra.membership)
          return null
        return this.invoice.recipient.extra.membership
      },
      showAdditionalPlayers() {
        if (!this.pkg) return false
        return this.pkg.ask_for_players == 'up_to_four' ||
          this.pkg.ask_for_players == 'singles_or_doubles' && this.player_addons.length > 0
      },
      singleOrDoublesOptions() {
        return [
          {value: 'singles', text: trans('singles')},
          {value: 'doubles', text: trans('doubles')},
        ]
      },
      upToFourOptions() {
        return [
          {value: 0, text: '1 ' + trans('player').toLowerCase()},
          {value: 1, text: '2 ' + trans('players').toLowerCase()},
          {value: 2, text: '3 ' + trans('players').toLowerCase()},
          {value: 3, text: '4 ' + trans('players').toLowerCase()},
        ]
      },
      playerAddonOptions() {
        return _.map(this.modifier_addons, addon => {
          return {
            value: addon.id,
            text: addon.name
          }
        })
      },
      bookingFullPrice() {
        if (!this.package_invoice_item || !this.invoice) return null
        return _.round(_.reduce(this.invoice.invoice_items, (sum, ii) => {
          if (ii.invoice_item_id == this.package_invoice_item.id) sum += _.round(ii.price, 2)
          return sum
        }, _.round(this.package_invoice_item.price, 2)), 2)
      },
      playerOnePrice() {
        if (this.pkg.requires_coupon) return 0
        if (this.pkg.adjust_price_per_player) return _.round(this.package_invoice_item.price, 2)
        return this.bookingFullPrice
      },
    },
    watch: {
      package_invoice_item() {
        this.deserializeAddons()
      },
    },
    methods: {
      onConnected(invoice) {
        this.$emit('invoice', invoice)
        this.$nextTick(() => {
          if (invoice.recipient) this.member_id = invoice.recipient.id
        })
        this.$emit('client-changed')
      },
      onClickApplyExistingCoupon() {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/package/${this.package_invoice_item.id}/apply_existing_coupon`
        axios.post(url)
        .then(response => {
          this.busy = false
          this.show_apply_coupon = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onClickChoosePlayer(addon) {
        this.player_addon_invoice_item = !!addon.invoice_item_id ?
          _.find(this.invoice.invoice_items, {id: addon.invoice_item_id}) || null :
          null
        if (!!this.player_addon_invoice_item) {
          this.$bvModal.show('package-reservation-choose-player-modal')
        }
      },
      deserializeAddons() {
        this.addons = _.reduce(this.package_invoice_item.addons, (addons, addon) => {
          if (!addon.type &&
            !isAddonHidden(addon, this.pkg.addon_rules, this.package_invoice_item.booking.events))
            addons.push(_.extend({original_quantity: addon.quantity}, addon))
          return addons
        }, [])
        this.modifier_addons = _.reverse(_.sortBy(_.reduce(this.pkg.addons, (addons, addon) => {
          if (addon.type == 'modifier' &&
            !isAddonHidden(addon, this.pkg.addon_rules, this.package_invoice_item.booking.events))
            addons.push(addon)
          return addons
        }, []), 'price'))
        this.player_addons = _.reduce(this.invoice.invoice_items || [], (addons, ii) => {
          if (ii.invoice_item_id == this.package_invoice_item.id) {
            let modifier_addon = _.find(this.modifier_addons, {id: ii.addon_id})
            if (!!modifier_addon) {
              addons.push({
                invoice_item_id: ii.id,
                form_answer_id: ii.form_answer_id,
                form_question_id: ii.form_question_id,
                package_invoice_item_id: ii.invoice_item_id,
                addon_id: ii.addon_id,
                price: ii.price,
                quantity: 1,
                name: ii.name,
                addon_name: modifier_addon.name,
              })
            }
          }
          return addons
        }, [])
        this.singles_or_doubles = this.player_addons.length > 1 ? 'doubles' : 'singles'
        this.up_to_four = this.player_addons.length
      },
      onChangeSinglesOrDoubles() {
        let addon = this.modifier_addons[0]
        let count = this.singles_or_doubles == 'singles' ? 2 : 4
        if (this.pkg.ask_for_players == 'up_to_four') count = this.up_to_four
        let price = this.pkg.adjust_price_per_player ? addon.price : this.playerOnePrice
        let player_addon = {
          invoice_item_id: null,
          form_answer_id: null,
          form_question_id: null,
          package_invoice_item_id: this.package_invoice_item.id,
          addon_id: addon.id,
          price: price / count,
          quantity: 1,
          name: addon.name,
          addon_name: addon.name,
        }

        if (!this.pkg.adjust_price_per_player) {
          _.each(this.player_addons, pa => {
            pa.price = price / count
          })
        }

        let player_addons = []
        if (this.pkg.ask_for_players == 'singles_or_doubles' ||
          this.pkg.ask_for_players == 'up_to_four' && this.up_to_four >= 1) {
          // Create the first addon (2 players or more)
          player_addons = [
            this.player_addons[0] || _.clone(player_addon),
          ]
        }
        if (this.pkg.ask_for_players == 'up_to_four' && this.up_to_four == 2) {
          // Ensure 2 addons (3 players)
          player_addons.push(this.player_addons[1] || _.clone(player_addon))
        }
        if (this.pkg.ask_for_players == 'singles_or_doubles' && this.singles_or_doubles == 'doubles' ||
          this.pkg.ask_for_players == 'up_to_four' && this.up_to_four == 3) {
          // Ensure 3 addons (4 players)
          player_addons.push(this.player_addons[1] || _.clone(player_addon))
          player_addons.push(this.player_addons[2] || _.clone(player_addon))
        }

        this.player_addons = player_addons
        this.$nextTick(this.savePlayerAddons)
      },
      onChangePlayerAddon(index, addon_id) {
        let player_addon = this.player_addons[index]
        let addon = _.find(this.modifier_addons, {id: addon_id})
        let count = this.player_addons.length + 1
        player_addon.addon_id = addon.id
        player_addon.name = addon.name
        player_addon.addon_name = addon.name
        player_addon.price = addon.price / count
        this.$set(this.player_addons, index, player_addon)
        this.$nextTick(this.savePlayerAddons)
      },
      onCloseApplyCoupon() {
        this.show_apply_coupon = false
      },
      onCouponApplied(invoice) {
        this.$emit('invoice', invoice)
        this.onCloseApplyCoupon()
      },
      removeCoupon(package_invoice_item) {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/package/${this.package_invoice_item.id}/coupon`
        axios.delete(url)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      updateItemInCart(package_invoice_item) {
        this.busy = true
        this.error = null
        axios.patch(`/org/${this.organization.id}/cart/package/${package_invoice_item.id}`, {
          start: this.package_invoice_item.start,
        })
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      removeFromCart(package_invoice_item) {
        this.busy = true
        this.error = null
        axios.delete(`/org/${this.organization.id}/cart/package/${package_invoice_item.id}`)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
          this.$emit('removed')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      savePlayerAddons() {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/player_addons/${this.package_invoice_item.id}`

        axios.patch(url, {player_addons: this.player_addons})
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      getAddonQuantityState(package_invoice_item, addon) {
        if (!_.isNumeric(addon.quantity) && addon.required) return false
        if (_.isNumeric(addon.quantity) && _.isNumeric(addon.min_quantity) && addon.quantity < addon.min_quantity) return false
        if (_.isNumeric(addon.quantity) && _.isNumeric(addon.max_quantity) && addon.quantity > addon.max_quantity) return false
        return null
      },
      onAddAddonToCart(package_invoice_item, addon) {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/addon`
        axios.post(url, {
          invoice_item_id: package_invoice_item.id,
          addon_id: addon.id,
          quantity: addon.quantity,
        })
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onModifyAddonQuantityInCart(package_invoice_item, addon) {
        if (addon.quantity == 0 && !addon.invoice_item_id) return
        if (addon.quantity == 0) return this.onRemoveAddonFromCart(package_invoice_item, addon)
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/addon/${addon.invoice_item_id}`
        axios.patch(url, {
          quantity: addon.quantity,
        })
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onRemoveAddonFromCart(package_invoice_item, addon) {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/addon/${addon.invoice_item_id}`
        axios.delete(url)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      }
    },
    mounted() {
      window['packageReservationAdded_' + this.package_invoice_item.id] = this
      this.deserializeAddons()
    },
  }
</script>
